/*
 * Manta inquire — opens a shared popup form for sold-out products.
 *
 * The form is submitted NATIVELY through Shopify's contact form (same delivery as the
 * contact page). We deliberately do NOT hijack the submit with fetch(): the store has
 * form spam protection (reCAPTCHA) enabled, and a background fetch has no captcha token,
 * so it is rejected while Shopify's own handler submits a second time — causing a false
 * error, a double submission and the popup closing on navigation.
 *
 * Instead, on submit we remember the product in sessionStorage and let Shopify handle it.
 * Shopify validates, sends the email and redirects back to the same page with
 * `?contact_posted=true`; on that return we re-open the popup showing the confirmation.
 * Client-side validation (required / type="email") keeps the popup open for user input
 * errors without ever submitting, so the popup has no error state of its own.
 */
(function () {
  'use strict';

  var MODAL_SELECTOR = '#MantaInquire-Modal';
  var STORAGE_KEY = 'manta-inquire:return';
  // Only honour a stored submission for a limited window so an abandoned captcha challenge
  // can't surface a stale confirmation later. Long enough to solve a captcha puzzle.
  var RETURN_MAX_AGE = 10 * 60 * 1000;

  function absoluteUrl(url) {
    if (!url) return '';
    try {
      return new URL(url, window.location.origin).href;
    } catch (err) {
      return url;
    }
  }

  function resetFeedback(modal) {
    var success = modal.querySelector('[data-manta-inquire-success]');
    var message = modal.querySelector('.manta-inquire__message');
    var form = modal.querySelector('form');
    if (success) success.hidden = true;
    if (message) message.hidden = false;
    if (form) form.hidden = false;
  }

  function populateModal(modal, data) {
    if (!modal) return;

    var title = data.productTitle || '';
    var url = data.productUrl || '';
    var image = data.productImage || '';
    var sku = data.productSku || '';

    var vendor = data.productVendor || '';
    var vendorUrl = data.productVendorUrl || '';

    var titleEl = modal.querySelector('[data-manta-inquire-product-title]');
    if (titleEl) titleEl.textContent = title;

    var linkEl = modal.querySelector('[data-manta-inquire-product-link]');
    if (linkEl) {
      if (url) {
        linkEl.setAttribute('href', url);
      } else {
        linkEl.removeAttribute('href');
      }
    }

    var vendorEl = modal.querySelector('[data-manta-inquire-product-vendor]');
    if (vendorEl) {
      if (vendor) {
        // vendor holds the localised "By {vendor}" markup rendered server-side.
        vendorEl.innerHTML = vendor;
        if (vendorUrl) {
          vendorEl.setAttribute('href', vendorUrl);
        } else {
          vendorEl.removeAttribute('href');
        }
        vendorEl.hidden = false;
      } else {
        vendorEl.innerHTML = '';
        vendorEl.hidden = true;
      }
    }

    var imageWrap = modal.querySelector('[data-manta-inquire-product-image]');
    if (imageWrap) {
      if (image) {
        var img = document.createElement('img');
        img.src = image;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 64;
        img.height = 64;
        imageWrap.innerHTML = '';
        imageWrap.appendChild(img);
        imageWrap.hidden = false;
      } else {
        imageWrap.innerHTML = '';
        imageWrap.hidden = true;
      }
    }

    var productField = modal.querySelector('[data-manta-inquire-product-field]');
    if (productField) {
      var parts = [];
      if (title) parts.push(title);
      if (sku) parts.push('SKU: ' + sku);
      if (url) parts.push(absoluteUrl(url));
      productField.value = parts.join(' | ');
    }

    // Selected variant options (product page / quick view only): one capsule per option
    // in the popup, and a single "Name: Value; Name: Value" line in the email.
    var options = Array.isArray(data.productOptions) ? data.productOptions : [];
    modal.mantaInquireOptions = options;

    var optionsList = modal.querySelector('[data-manta-inquire-product-options]');
    if (optionsList) {
      optionsList.innerHTML = '';
      options.forEach(function (option) {
        var item = document.createElement('li');
        item.className = 'manta-inquire__option';
        item.textContent = option.name + ': ' + option.value;
        optionsList.appendChild(item);
      });
      optionsList.hidden = options.length === 0;
    }

    var variantField = modal.querySelector('[data-manta-inquire-variant-field]');
    if (variantField) {
      variantField.value = options
        .map(function (option) {
          return option.name + ': ' + option.value;
        })
        .join('; ');
      // Disabled inputs aren't submitted, so products without variants get no empty line.
      variantField.disabled = options.length === 0;
    }

    resetFeedback(modal);
  }

  function parseOptions(raw) {
    if (!raw) return [];
    try {
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.filter(function (option) {
            return option && option.name && option.value;
          })
        : [];
    } catch (err) {
      return [];
    }
  }

  // Read the product currently shown in the modal so it can be restored in the
  // confirmation after Shopify redirects back.
  function readModalProduct(modal) {
    var titleEl = modal.querySelector('[data-manta-inquire-product-title]');
    var linkEl = modal.querySelector('[data-manta-inquire-product-link]');
    var vendorEl = modal.querySelector('[data-manta-inquire-product-vendor]');
    var img = modal.querySelector('[data-manta-inquire-product-image] img');
    return {
      productTitle: titleEl ? titleEl.textContent : '',
      productUrl: linkEl ? linkEl.getAttribute('href') || '' : '',
      productImage: img ? img.getAttribute('src') || '' : '',
      productVendor: vendorEl && !vendorEl.hidden ? vendorEl.innerHTML : '',
      productVendorUrl: vendorEl ? vendorEl.getAttribute('href') || '' : '',
      productOptions: modal.mantaInquireOptions || []
    };
  }

  function showConfirmation(modal, data) {
    populateModal(modal, data);
    var form = modal.querySelector('form');
    var successEl = modal.querySelector('[data-manta-inquire-success]');
    var message = modal.querySelector('.manta-inquire__message');
    if (form) form.hidden = true;
    if (message) message.hidden = true;
    if (successEl) successEl.hidden = false;
    if (typeof modal.show === 'function') modal.show();
  }

  if (!customElements.get('manta-inquire-opener')) {
    customElements.define(
      'manta-inquire-opener',
      class MantaInquireOpener extends HTMLElement {
        constructor() {
          super();
          this.button = this.querySelector('button');
          if (this.button) {
            this.button.addEventListener('click', this.open.bind(this));
          }
        }

        open() {
          var modal = document.querySelector(this.getAttribute('data-modal') || MODAL_SELECTOR);
          if (!modal) return;
          bindForm(modal);
          populateModal(modal, {
            productTitle: this.getAttribute('data-product-title'),
            productUrl: this.getAttribute('data-product-url'),
            productImage: this.getAttribute('data-product-image'),
            productSku: this.getAttribute('data-product-sku'),
            productVendor: this.getAttribute('data-product-vendor'),
            productVendorUrl: this.getAttribute('data-product-vendor-url'),
            productOptions: parseOptions(this.getAttribute('data-product-options'))
          });
          if (typeof modal.show === 'function') {
            modal.show(this.button);
          }
        }
      }
    );
  }

  function bindForm(modal) {
    if (!modal) return;

    var form = modal.querySelector('form');
    if (!form || form.dataset.mantaInquireBound) return;
    form.dataset.mantaInquireBound = 'true';

    var submitBtn = form.querySelector('[type="submit"]');
    var submitLabel = submitBtn ? submitBtn.querySelector('[data-manta-inquire-submit-label]') : null;
    var sendingText = submitBtn ? submitBtn.getAttribute('data-sending-text') : '';

    // Capture phase so this runs before Shopify's spam-protection handler, guaranteeing the
    // return marker is stored even if that handler later stops propagation. We never call
    // preventDefault — Shopify owns the submission (captcha + delivery + redirect).
    form.addEventListener(
      'submit',
      function () {
        // NOTE: leave the form action as Shopify rendered it (/contact). Shopify only
        // accepts contact POSTs there; posting to a product/collection path returns 404.
        // On success Shopify redirects back to the referring page with ?contact_posted=true,
        // where handleReturn() re-opens this popup with the confirmation.
        try {
          sessionStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ time: Date.now(), product: readModalProduct(modal) })
          );
        } catch (err) {
          /* sessionStorage unavailable — the form still submits, just no re-opened confirmation */
        }

        if (submitBtn) {
          submitBtn.setAttribute('aria-busy', 'true');
          if (submitLabel && sendingText) submitLabel.textContent = sendingText;
        }
      },
      true
    );

    // Coming Back (e.g. from the captcha page) restores this page from the back/forward
    // cache with the button still locked in its "Sending…" state — unlock it.
    var idleText = submitLabel ? submitLabel.textContent : '';
    window.addEventListener('pageshow', function (evt) {
      if (!evt.persisted || !submitBtn) return;
      submitBtn.removeAttribute('aria-busy');
      if (submitLabel && idleText) submitLabel.textContent = idleText;
    });
  }

  // After Shopify redirects back, re-open the popup with the confirmation.
  // The marker is only consumed once Shopify's success signal arrives: the captcha
  // challenge page also renders this theme, and clearing the marker there would lose the
  // confirmation for shoppers who had to solve a captcha puzzle.
  function handleReturn() {
    var stored;
    try {
      stored = sessionStorage.getItem(STORAGE_KEY);
    } catch (err) {
      stored = null;
    }
    if (!stored) return;

    var payload;
    try {
      payload = JSON.parse(stored);
    } catch (err) {
      payload = null;
    }
    var expired = !payload || !payload.time || Date.now() - payload.time > RETURN_MAX_AGE;
    var posted = window.location.search.indexOf('contact_posted=true') !== -1;

    if (expired || posted) {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (err) {
        /* ignore */
      }
    }
    if (expired || !posted) return;

    var modal = document.querySelector(MODAL_SELECTOR);
    if (!modal) return;
    bindForm(modal);
    showConfirmation(modal, payload.product || {});

    // Drop contact_posted from the URL so a refresh doesn't look like a fresh submission.
    try {
      var params = new URLSearchParams(window.location.search);
      params.delete('contact_posted');
      var query = params.toString();
      window.history.replaceState(
        {},
        '',
        window.location.pathname + (query ? '?' + query : '') + window.location.hash
      );
    } catch (err) {
      /* history API unavailable — harmless */
    }
  }

  function init() {
    bindForm(document.querySelector(MODAL_SELECTOR));
    handleReturn();
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
